import React from 'react';
import axios from 'axios';
import { useQuery } from 'react-query';
import Grid, { GridItem } from '@/components/layout/Grid';
import VBox from '@/components/layout/VBox';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoArea from '@/components/Formulario/CampoArea';
import CampoNumero from '@/components/Formulario/CampoNumero';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import CampoCheckbox from '@/components/Formulario/CampoCheckbox';
import AlertBox from '@/components/layout/AlertBox';
import Skeleton from '@/components/layout/Skeleton';
import { sugerirChave } from '@/components/EditorCampos';
import SeletorCampo from './SeletorCampo';
import {
    Catalogo,
    CatalogoConfig,
    EstruturaCatalogo,
    FonteCatalogo,
    MAPA_PADRAO_PRODUTO,
    MapaCamposCatalogo,
} from '@tera/shared/src/types/Catalogo';
import CampoMultiTexto from '@/components/Formulario/CampoMultiTexto';
import CampoMemo from '@/components/Formulario/CampoMemo';

type Props = {
    value?: Catalogo | null;
    onChange?: (value: Catalogo) => void;
    fieldErrors?: Record<string, string>;
};

const FONTES: { id: FonteCatalogo; label: string }[] = [
    { id: 'dataset', label: 'Tabela de dados' },
    { id: 'produto', label: 'Produtos do CRM' },
    { id: 'integration', label: 'Integração (API externa)' },
];

/**
 * Atributos que o motor de orçamento precisa de cada item. São estes que o
 * formulário concilia com os campos reais da fonte.
 */
const ATRIBUTOS: { chave: keyof MapaCamposCatalogo; label: string; obrigatorio: boolean }[] = [
    { chave: 'codigo', label: 'Código', obrigatorio: true },
    { chave: 'nome', label: 'Nome', obrigatorio: true },
    { chave: 'preco', label: 'Preço', obrigatorio: true },
    { chave: 'descricao', label: 'Descrição', obrigatorio: false },
    { chave: 'unidade', label: 'Unidade', obrigatorio: false },
];

const ESTRUTURA_VAZIA: EstruturaCatalogo = { campos: [], origem: 'schema' };

const FormCatalogo = ({ value, onChange, fieldErrors }: Props) => {
    const config = (value?.config ?? { fonte: 'dataset' }) as any;
    const fonte: FonteCatalogo = config.fonte ?? 'dataset';

    // Hooks antes de qualquer retorno antecipado — o modal monta este
    // componente com `value` nulo enquanto nada está selecionado.
    const datasets = useQuery(
        ['dataset/box'],
        async () => (await axios.get('/api/dataset/box')).data.result,
        { enabled: !!value && fonte === 'dataset' },
    );

    const integracoes = useQuery(
        ['integrations/box'],
        async () => (await axios.get('/api/integrations/box')).data.result ?? [],
        { enabled: !!value && fonte === 'integration' },
    );

    /**
     * A estrutura depende só das partes da config que identificam a fonte —
     * não do mapa em si, senão cada tecla no mapeamento dispararia uma nova
     * chamada (que, para integração, bate na API externa).
     */
    const chaveEstrutura = JSON.stringify({
        fonte,
        dataset_chave: config.dataset_chave,
        integrationId: config.integrationId,
        action: config.action,
        template_key: config.template_key,
        jsonpathLista: config.jsonpathLista,
    });

    const estrutura = useQuery(
        ['catalogo/estrutura', chaveEstrutura],
        async () =>
            (await axios.post('/api/catalogo/estrutura', { config })).data
                .result as EstruturaCatalogo,
        { enabled: !!value, keepPreviousData: true },
    );

    if (!value) return null;

    const novo = !value.id;
    const dados = estrutura.data ?? ESTRUTURA_VAZIA;

    const chaveAcompanhaNome =
        novo && (!value.chave || value.chave === sugerirChave(value.nome ?? ''));

    const alterar = (mudanca: Partial<Catalogo>) =>
        onChange?.({ ...value, ...mudanca } as Catalogo);

    const alterarConfig = (mudanca: Record<string, any>) =>
        alterar({ config: { ...config, ...mudanca } as CatalogoConfig });

    const alterarMapa = (campo: keyof MapaCamposCatalogo, v: string) => {
        // Integração guarda JSONPath relativo ao item; as demais, nome de campo.
        const chaveMapa = fonte === 'integration' ? 'jsonpath' : 'mapa';
        alterarConfig({ [chaveMapa]: { ...(config[chaveMapa] ?? {}), [campo]: v || undefined } });
    };

    const mapaAtual = (fonte === 'integration' ? config.jsonpath : config.mapa) ?? {};
    // Produto sai da caixa com as colunas fixas já mapeadas.
    const mapa = fonte === 'produto' ? { ...MAPA_PADRAO_PRODUTO, ...mapaAtual } : mapaAtual;

    const renderMapa = () => (
        <GridItem sm={12}>
            <h3>Mapeamento dos campos</h3>
            <p>
                Associe cada atributo do orçamento a um campo da fonte.{' '}
                {dados.origem === 'amostra'
                    ? 'A lista vem de um item real devolvido pela integração, então mostra apenas o que veio naquele item.'
                    : 'A lista vem do cadastro da fonte.'}{' '}
                Item sem código ou sem preço utilizável é ignorado na cotação — melhor
                não oferecer do que oferecer com preço errado.
            </p>

            {estrutura.isLoading && !dados.campos.length && <Skeleton format="box" />}

            {!!dados.aviso && (
                <AlertBox variant="warning">
                    <p>
                        {dados.aviso} Você ainda pode digitar o campo manualmente no botão ao
                        lado de cada atributo.
                    </p>
                </AlertBox>
            )}

            <Grid style={{ marginTop: '16px' }}>
                {ATRIBUTOS.map((attr) => (
                    <GridItem sm={12} md={4} key={attr.chave}>
                        <SeletorCampo
                            instanceId={`catalogo-mapa-${attr.chave}`}
                            label={`${attr.label}${attr.obrigatorio ? ' *' : ''}`}
                            value={mapa[attr.chave]}
                            opcoes={dados.campos}
                            onChange={(v: string) => alterarMapa(attr.chave, v)}
                            carregando={estrutura.isLoading}
                            placeholder={fonte === 'integration' ? '$.preco' : 'preco'}
                            error={fieldErrors?.[`config.mapa.${attr.chave}`]}
                        />
                    </GridItem>
                ))}
            </Grid>
        </GridItem>
    );

    return (
        <Grid>
            <GridItem sm={12} md={6}>
                <CampoTexto
                    instanceId="catalogo-nome"
                    label="Nome"
                    value={value.nome ?? ''}
                    onChange={(v: string) =>
                        alterar({
                            nome: v,
                            // Depois de criado a chave é imutável: nós de fluxo, agentes
                            // de IA e campos de formulário referenciam o catálogo por ela.
                            chave: chaveAcompanhaNome ? sugerirChave(v) : value.chave,
                        })
                    }
                    error={fieldErrors?.nome}
                />
            </GridItem>
            <GridItem sm={12} md={6}>
                <CampoTexto
                    instanceId="catalogo-chave"
                    label="Chave"
                    value={value.chave ?? ''}
                    onChange={(v: string) => alterar({ chave: sugerirChave(v) })}
                    disabled={!novo}
                    error={fieldErrors?.chave}
                />
            </GridItem>

            <GridItem sm={12}>
                <CampoMemo
                    instanceId="catalogo-descricao"
                    label="Descrição"
                    value={value.descricao ?? ''}
                    onChange={(v: string) => alterar({ descricao: v })}
                    rows={2}
                    error={fieldErrors?.descricao}
                />
            </GridItem>

            <GridItem sm={12} md={4}>
                <CampoSeletor
                    instanceId="catalogo-fonte"
                    label="Fonte dos itens"
                    options={FONTES.map((f) => ({ id: f.id, label: f.label }))}
                    value={fonte}
                    // Trocar a fonte descarta o mapeamento anterior: os campos de uma
                    // tabela não significam nada como JSONPath, e vice-versa.
                    onChange={(v: string | null) =>
                        alterar({ config: { fonte: (v ?? 'dataset') as FonteCatalogo } as CatalogoConfig })
                    }
                />
            </GridItem>
            <GridItem sm={12} md={4}>
                <CampoTexto
                    instanceId="catalogo-moeda"
                    label="Moeda"
                    value={value.moeda ?? 'BRL'}
                    onChange={(v: string) => alterar({ moeda: v.toUpperCase().slice(0, 3) })}
                    error={fieldErrors?.moeda}
                />
            </GridItem>
            <GridItem sm={12} md={4}>
                <CampoNumero
                    instanceId="catalogo-validade"
                    label="Validade do orçamento (dias)"
                    value={value.validade_dias != null ? String(value.validade_dias) : ''}
                    decimais={0}
                    somentePositivos
                    onChange={(v: string) =>
                        alterar({ validade_dias: v ? Number(v) : null })
                    }
                    error={fieldErrors?.validade_dias}
                />
            </GridItem>

            {fonte === 'dataset' && (
                <>
                    <GridItem sm={12} md={6}>
                        <CampoSeletor
                            instanceId="catalogo-dataset"
                            label="Tabela de dados"
                            options={(datasets.data ?? []).map((d: any) => ({
                                id: d.chave,
                                label: d.nome,
                            }))}
                            value={config.dataset_chave ?? null}
                            onChange={(v: string | null) => alterarConfig({ dataset_chave: v })}
                            loadingState={datasets.isLoading ? 'loading' : 'success'}
                        />
                    </GridItem>
                    {renderMapa()}
                </>
            )}

            {fonte === 'produto' && (
                <>
                    <GridItem sm={12}>
                        <AlertBox variant="info">
                            <p>
                                Os itens vêm do cadastro de Produtos do CRM. As colunas fixas já
                                vêm mapeadas; troque apenas se o dado real estiver nos{' '}
                                <strong>metadados</strong> do produto — um SKU, por exemplo.
                            </p>
                        </AlertBox>
                    </GridItem>
                    {renderMapa()}
                </>
            )}

            {fonte === 'integration' && (
                <>
                    <GridItem sm={12} md={4}>
                        <CampoSeletor
                            instanceId="catalogo-integracao"
                            label="Integração"
                            options={(integracoes.data ?? []).map((i: any) => ({
                                id: String(i.id),
                                label: i.name ?? i.nome ?? `#${i.id}`,
                            }))}
                            value={config.integrationId ? String(config.integrationId) : null}
                            onChange={(v: string | null) => {
                                const escolhida = (integracoes.data ?? []).find(
                                    (i: any) => String(i.id) === v,
                                );
                                // Guarda o template junto: `saidasDeclaradas` precisa saber
                                // se a ação é de endpoint custom ou de template.
                                alterarConfig({
                                    integrationId: v ? Number(v) : undefined,
                                    template_key: escolhida?.template_key,
                                    template_version: escolhida?.template_version,
                                    action: undefined,
                                });
                            }}
                            loadingState={integracoes.isLoading ? 'loading' : 'success'}
                        />
                    </GridItem>
                    <GridItem sm={12} md={4}>
                        <CampoSeletor
                            instanceId="catalogo-action"
                            label="Ação"
                            nullable
                            options={(
                                (integracoes.data ?? []).find(
                                    (i: any) => String(i.id) === String(config.integrationId),
                                )?.actions ?? []
                            ).map((a: any) => ({
                                id: a.name,
                                label: a.description ? `${a.name} — ${a.description}` : a.name,
                            }))}
                            value={config.action ?? null}
                            onChange={(v: string | null) => alterarConfig({ action: v ?? undefined })}
                        />
                    </GridItem>
                    <GridItem sm={12} md={4}>
                        <SeletorCampo
                            instanceId="catalogo-jsonpath-lista"
                            label="Onde está a lista de itens *"
                            value={config.jsonpathLista}
                            opcoes={dados.listas ?? []}
                            onChange={(v: string) => alterarConfig({ jsonpathLista: v })}
                            carregando={estrutura.isLoading}
                            placeholder="$.itens"
                        />
                    </GridItem>
                    {renderMapa()}
                    <GridItem sm={12}>
                        <AlertBox variant="info">
                            <p>
                                Os campos acima são descobertos consultando a integração de
                                verdade e lendo o primeiro item da lista. A busca por texto é
                                aplicada em memória sobre o que a ação devolve; para pré-filtrar
                                no lado do ERP, use os parâmetros fixos da integração.
                            </p>
                        </AlertBox>
                    </GridItem>
                </>
            )}

            <GridItem sm={12}>
                <VBox gap={8}>
                    <CampoCheckbox
                        instanceId="catalogo-ativo"
                        label="Catálogo ativo"
                        value={value.ativo !== false}
                        onChange={(v: boolean) => alterar({ ativo: v })}
                    />
                </VBox>
            </GridItem>
        </Grid>
    );
};

export default FormCatalogo;
